import React, { useState, useEffect } from 'react';
import { getAllUsers, getUserDetails } from '../../services/api';
import UserManagement from '../../components/UserManagement/UserManagement';
import UserDetail from '../../components/UserManagement/UserDetail';
import TopNavigation from '../../components/TopNavigation/TopNavigation';
import './UserManagementPage.css';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const response = await getAllUsers();
        setUsers(response.data);
        // Auto-select first user if available
        if (response.data.length > 0) {
          handleSelectUser(response.data[0].id);
        }
      } catch (err) {
        console.error('Error loading users:', err);
        setError('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, []);
  
  // Handle when user is selected from list
  const handleSelectUser = async (userId) => {
    setIsLoading(true);
    try {
      const response = await getUserDetails(userId);
      setSelectedUser(response.data);
    } catch (err) {
      console.error('Error loading user details:', err);
      setError('Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle after user is updated (refresh data)
  const handleUserUpdated = async (updatedUser) => {
    console.log('handleUserUpdated called with:', updatedUser);
    if (updatedUser) {
      // Update the selected user directly
      setSelectedUser(updatedUser);
      // Update user in the list
      setUsers(prevUsers => {
        const updatedUsers = prevUsers.map(user => 
          user.id === updatedUser.id ? updatedUser : user
        );
        console.log('Updated users list:', updatedUsers);
        return updatedUsers;
      });
    } else if (selectedUser) {
      // Fallback: refetch data if no updated user provided
      console.log('Fallback: refetching user data');
      await handleSelectUser(selectedUser.id);
    }
  };

  // Handle after new user is created (add to list)
  const handleUserCreated = async (newUser) => {
    if (newUser) {
      // Add new user to the list
      setUsers(prevUsers => [...prevUsers, newUser]);
      // Select the newly created user
      setSelectedUser(newUser);
    }
  };

  // Handle after user is deleted (remove from list)
  const handleUserDeleted = async (deletedUserId) => {
    // Remove user from the list
    setUsers(prevUsers => prevUsers.filter(user => user.id !== deletedUserId));
    
    // If the deleted user was selected, clear selection
    if (selectedUser && selectedUser.id === deletedUserId) {
      setSelectedUser(null);
    }
  };

  return (
    <div className="user-management-page">
      <TopNavigation />
      <div className="split-view-container">
        <div className="list-panel">
          {error && <div className="error-message">{error}</div>}
          {isLoading && !users.length ? (
            <div className="loading-state">Loading users...</div>
          ) : (
            <UserManagement 
              users={users} 
              selectedUserId={selectedUser?.id}
              onSelectUser={handleSelectUser}
              onUserCreated={handleUserCreated}
              onUserUpdated={handleUserUpdated}
              onUserDeleted={handleUserDeleted}
            />
          )}
        </div>
        <div className="detail-panel">
          {selectedUser ? (
            <>
              <h2>User Details</h2>
              {isLoading ? (
                <div className="loading-state">Loading details...</div>
              ) : (
                <UserDetail 
                  user={selectedUser} 
                  onUserUpdated={handleUserUpdated}
                />
              )}
            </>
          ) : (
            <div className="empty-state">Select a user to view details</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementPage;
